import { useMemo, FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useStore } from '@hooks/useStore';
import { getDate } from '@utils/convertString';
import { useMyQuery } from '@hooks/useMyQuery';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useOnEntityClick } from '@hooks/useOnEntityClick';
import { useFilterEntity } from '@hooks/useFilterEntity';
import Table from '@components/Core/Table/Table';
import { EntityIcon } from '@components/Core/EntityIcon/EntityIcon';
import { StatusBadge } from '@components/Core/StatusBadge';
import Popup from '@components/Core/Popup/Popup';
import { NoResults } from '@components/Core/NoResults';
import { Button } from '@components/Core/Button';
import { AppLoader } from '@components/Core/AppLoader';
import { StatusPopup } from '@popups/StatusPopup';
import SettingsIcon from '@icons/settings_icon.svg?component';
import { CataloguesTransFields } from '@pages/Catalogues/types';
import { cataloguesTranslations } from '@pages/Catalogues/translations';
import { ICatalogueListData } from '@stores/catalogues/types';
import { CataloguesTableTransFields } from './types';
import { cataloguesTableTranslations } from './translations';

const columnHelper = createColumnHelper<ICatalogueListData>();

const CataloguesTable: FC<{ search: string }> = observer(({ search }) => {
  const {
    cataloguesStore: { getCataloguesList, cataloguesList },
    mainStore: { selectedCountryCode },
    popupStore: { popupName },
  } = useStore();

  const { brandId } = useParams();

  const { isFetching } = useMyQuery({
    queryKey: ['getCataloguesList'],
    queryFn: () => (brandId ? getCataloguesList(brandId) : Promise.resolve(undefined)), // remove promise when getCataloguesList will use real API
    enabled: !!selectedCountryCode || !!brandId, // wait until it's defined
    refetchOnMount: 'always',
  });

  const { t } = useMyTranslations<
    CataloguesTableTransFields | Extract<CataloguesTransFields, 'addCatalogue'>
  >([cataloguesTableTranslations.name, cataloguesTranslations.name]);

  const navigate = useNavigate();

  const data = useFilterEntity(cataloguesList, search);

  const { onClick, popupData } = useOnEntityClick<ICatalogueListData>();

  const columns: ColumnDef<ICatalogueListData, any>[] = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: t('name'),
        cell: ({
          row: {
            original,
            original: { previewUrl, name },
          },
        }) => (
          <button
            className="flex items-center w-full gap-x-[12px] bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <EntityIcon src={previewUrl} name="catalogue image" />
            <p className="whitespace-nowrap overflow-hidden text-ellipsis">{name || '—'}</p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true, className: 'min-w-[180px]' },
      }),
      columnHelper.accessor('startDate', {
        id: 'startDate',
        header: t('startDate'),
        cell: ({
          row: {
            original,
            original: { startDate },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <p className="text-left overflow-hidden text-ellipsis text-nowrap">
              {startDate ? getDate(startDate) : '-'}
            </p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true, className: 'min-w-[100px]' },
      }),
      columnHelper.accessor('expiredDate', {
        id: 'expiredDate',
        header: t('expiredDate'),
        cell: ({
          row: {
            original,
            original: { expiredDate },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <p className="text-left overflow-hidden text-ellipsis text-nowrap">
              {expiredDate ? getDate(expiredDate) : '-'}
            </p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true, className: 'min-w-[100px]' },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: t('status'),
        cell: ({
          row: {
            original,
            original: { status },
          },
        }) => (
          <button
            className="w-full h-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <StatusBadge status={status} />
          </button>
        ),
        enableSorting: true,
        meta: { width: 80 },
      }),
      {
        id: 'option',
        header: t('option'),
        cell: ({ row: { original } }) => (
          <button
            className="w-full h-full bg-none cursor-pointer flex-centred"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <SettingsIcon height={24} width={24} />
          </button>
        ),
        enableSorting: false,
        meta: { width: 56 },
      },
    ],
    [isFetching]
  );

  if (isFetching) return <AppLoader />;
  if ((data.length === 0 && search) || cataloguesList?.length === 0)
    return (
      <NoResults>
        <p className="small-text">{t('noResultText')}</p>
        <Button
          text={t('addCatalogue', { ns: cataloguesTranslations.name })}
          onClick={() => navigate(`/brands/${brandId}/add-catalogue`)}
          styleProp="max-w-[153px]"
        />
      </NoResults>
    );
  return (
    <>
      <Table columns={columns} data={data} />
      <Popup>{popupName === 'status' && popupData && <StatusPopup {...popupData} />}</Popup>
    </>
  );
});

export default CataloguesTable;
